import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { NgClass, NgComponentOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { FitLinesDirective } from '../../../@commons/ui-components/fit-lines/fit-lines.directive';
import { TagComponent } from '../../../@commons/ui-components/tag/tag.component';
import { LINE_ICONS } from '../../../@icons/line-icons';
import { CircleIconComponent } from '../../../@icons/circle-icon.component';
import { ThreeDotsIconComponent } from '../../../@icons/three-dots-icon.component';
import { DayMessage, DayTone } from './day-message.model';
import { DayTextPipe } from './day-text.pipe';

/** Fond et encre de chaque ton, aux jetons du thème. */
const TONES: Record<DayTone, { card: string; ink: string }> = {
  frost: { card: 'from-vw-cold-bg to-vw-surface', ink: 'text-vw-cold' },
  water: { card: 'from-vw-cold-bg to-vw-surface', ink: 'text-vw-cold' },
  wind: { card: 'from-vw-surface-sunk to-vw-surface', ink: 'text-vw-body' },
  go: { card: 'from-vw-surface-tint to-vw-bg', ink: 'text-vw-primary-ink' },
  calm: { card: 'from-vw-surface-tint to-vw-bg', ink: 'text-vw-primary-ink' },
  harvest: { card: 'from-vw-warn-bg to-vw-surface', ink: 'text-vw-warn-ink' },
  alert: { card: 'from-vw-warn-bg to-vw-surface', ink: 'text-vw-warn-ink' },
};

/** Un appui long ouvre la feuille de réponse, comme « … ». */
const LONG_PRESS_MS = 500;
/** Photos montrées dans la raison ; au-delà, la raison nomme. */
const MAX_PICTURES = 3;

/**
 * Le message du jour (`vw-day-message`) : la ligne du signal et sa source, le
 * titre en trois lignes au plus (Jost 26 px, puis 24 et 22, puis la forme
 * courte), la raison avec les photos des plantes et la pastille « estimé »,
 * puis le geste et « … ».
 *
 * Le bouton est une action (cercle vide), jamais un état ; « … » ou un appui
 * long ouvrent la feuille de réponse. Le composant ne répond rien lui-même :
 * `act` et `more` portent le message, l'accueil fait le reste.
 * Usage : `<vw-day-message [message]="choice.head" (act)="…" (more)="…" />`
 */
@Component({
  selector: 'vw-day-message',
  standalone: true,
  imports: [NgClass, NgComponentOutlet, TranslatePipe, SharedModule, FitLinesDirective, TagComponent, CircleIconComponent, ThreeDotsIconComponent, DayTextPipe],
  templateUrl: './day-message.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DayMessageComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) message!: DayMessage;
  /** Faux : ni geste ni réponse (essai invité, sans compte pour répondre). */
  @Input() actions = true;
  /** Le geste du message (« C'est couvert », « J'en ai cueilli »). */
  @Output() act = new EventEmitter<DayMessage>();
  /** « … » ou appui long : la feuille de réponse. */
  @Output() more = new EventEmitter<DayMessage>();

  /** La forme longue dépasse trois lignes au plus petit cran : la courte prend sa place jusqu'au message suivant. */
  useShort = false;
  readonly icons = LINE_ICONS;
  private pressTimer?: ReturnType<typeof setTimeout>;

  ngOnChanges(changes: SimpleChanges): void {
    const previous = changes['message']?.previousValue as DayMessage | undefined;
    if (previous && previous.key !== this.message.key) this.useShort = false;
  }

  ngOnDestroy(): void {
    clearTimeout(this.pressTimer);
  }

  get tone() {
    return TONES[this.message.tone] ?? TONES.calm;
  }

  /** La pastille « estimé » suit déjà le morceau de raison calculé. */
  get estimatedInReason(): boolean {
    return this.message.reason.some((part) => part.estimated);
  }

  /** Le texte sans son point final : la pastille « estimé » se pose avant lui. */
  withoutPeriod(text: string): string {
    return text.replace(/\.$/, '');
  }

  /** « C'est fait » pour une réponse ; le geste lui-même pour ouvrir ou noter (« J'en ai cueilli »). */
  get buttonLabel(): string {
    return this.message.button?.action === 'DONE' ? 'done' : this.message.button?.key ?? 'done';
  }

  /** Le bouton prend le ton du message : bleu pour le gel et l'eau, vert sinon. */
  get buttonTone(): 'primary' | 'cold' {
    return this.message.tone === 'frost' || this.message.tone === 'water' ? 'cold' : 'primary';
  }

  get pictures() {
    return this.message.plants.filter((p) => !!p.imageUrl).slice(0, MAX_PICTURES);
  }

  onOverflow(overflow: boolean): void {
    if (overflow) this.useShort = true;
  }

  pressStart(): void {
    clearTimeout(this.pressTimer);
    this.pressTimer = setTimeout(() => this.more.emit(this.message), LONG_PRESS_MS);
  }

  pressEnd(): void {
    clearTimeout(this.pressTimer);
  }

  /** Le menu du système (appui long d'une souris ou d'iOS) laisse place à la feuille. */
  onContextMenu(event: Event): void {
    if (!this.actions) return;
    event.preventDefault();
    this.pressEnd();
    this.more.emit(this.message);
  }
}
